import {
  Country,
  IncomeType,
  PensionersMaritalStatus,
  ResidencyStatus,
  TaxFileNumberExemptionType,
  TaxScaleType,
} from '$constants'
import { Nullable } from '$models'
import {
  formatBankAccountNumber,
  formatBSB,
  formatTaxFileNumber,
  transformStringToNumberProperty,
} from '$utils'
import {
  BaseStaffMember,
  StaffMember,
  transformBaseStaffMemberOnGet,
  transformBaseStaffMemberOnSave,
} from './staffMember'

export interface StaffMemberAu extends BaseStaffMember {
  taxFileNumber: Nullable<string>
  hasTaxFileNumberExemption?: boolean
  taxFileNumberExemptionType?: Nullable<TaxFileNumberExemptionType>
  residencyStatus?: Nullable<ResidencyStatus>
  incomeType?: Nullable<IncomeType>
  taxScaleType?: Nullable<TaxScaleType>
  claimingTaxFreeThreshold?: boolean
  claimingZoneOverseasOrInvalid?: boolean
  annualOffsetAmountClaimed?: Nullable<number>
  payeesWorkingCondition?: Nullable<string>
  hasStudyLoanOrFinancialDebt?: boolean
  pensionersMaritalStatus?: Nullable<PensionersMaritalStatus>
  superFundId?: Nullable<string>
  superMembershipNumber?: Nullable<string>
  notEligibleForSuperFund?: boolean
  superFundNameOrUsi?: Nullable<string>
  superFundSelfManaged?: boolean
  superFundMemberNumber?: Nullable<string>
  superFundABN?: Nullable<string>
  superFundESA?: Nullable<string>
  superFundAccountName?: Nullable<string>
  superFundAccountNumber?: Nullable<string>
  superFundBSB?: Nullable<string>
  increaseTaxWithholding?: boolean
  taxWithholdingAmount?: Nullable<number>
}

// Function to apply transformations to StaffMemberAu object on get
export const transformStaffMemberAuOnGet = (
  staffMember: StaffMember
): StaffMemberAu => {
  const transformedStaffMember: StaffMemberAu = transformBaseStaffMemberOnGet(
    staffMember
  ) as StaffMemberAu

  transformedStaffMember.bankAccountNumber = formatBankAccountNumber(
    transformedStaffMember.bankAccountNumber,
    Country.Australia
  )
  transformedStaffMember.superFundBSB = formatBSB(
    transformedStaffMember.superFundBSB
  )

  transformedStaffMember.taxFileNumber = formatTaxFileNumber(
    transformedStaffMember.taxFileNumber
  )

  //This is a workaround because the API is returning null for booleans if they are not set
  transformedStaffMember.hasTaxFileNumberExemption =
    transformedStaffMember.hasTaxFileNumberExemption ?? false
  transformedStaffMember.claimingTaxFreeThreshold =
    transformedStaffMember.claimingTaxFreeThreshold ?? false
  transformedStaffMember.claimingZoneOverseasOrInvalid =
    transformedStaffMember.claimingZoneOverseasOrInvalid ?? false
  transformedStaffMember.hasStudyLoanOrFinancialDebt =
    transformedStaffMember.hasStudyLoanOrFinancialDebt ?? false
  transformedStaffMember.superFundSelfManaged =
    transformedStaffMember.superFundSelfManaged ?? false
  transformedStaffMember.notEligibleForSuperFund =
    transformedStaffMember.notEligibleForSuperFund ?? false
  transformedStaffMember.increaseTaxWithholding =
    transformedStaffMember.increaseTaxWithholding ?? false

  return transformedStaffMember
}

// Function to apply transformations to StaffMemberAu object on get
export const transformStaffMemberAuOnSave = (
  staffMember: StaffMember
): object => {
  const staffMemberAu = staffMember as StaffMemberAu
  staffMemberAu.taxFileNumber = staffMemberAu.taxFileNumber?.replaceAll('-', '')
  staffMemberAu.superFundBSB = staffMemberAu.superFundBSB?.replaceAll('-', '')
  staffMemberAu.annualOffsetAmountClaimed =
    staffMemberAu.annualOffsetAmountClaimed
      ? transformStringToNumberProperty(
          `${staffMemberAu.annualOffsetAmountClaimed}`
        )
      : null
  staffMemberAu.taxWithholdingAmount = staffMemberAu.taxWithholdingAmount
    ? transformStringToNumberProperty(`${staffMemberAu.taxWithholdingAmount}`)
    : null
  const transformedStaffMember = transformBaseStaffMemberOnSave(staffMemberAu)

  return transformedStaffMember
}
